import React, { useEffect } from 'react';
import { addResourceHints, preloadResource, measurePerformance } from '@/utils/performance';
import { logger } from '@/utils/logger';

export const ProductionOptimizer: React.FC = () => {
  useEffect(() => {
    // Enhanced production optimizations
    if (process.env.NODE_ENV === 'production') {
      
      // Enhanced critical resource preloading
      const preloadCriticalResources = () => {
        const criticalResources = [
          // App icons
          { href: '/icon-192.png', as: 'image', priority: 'high' },
          { href: '/icon-512.png', as: 'image', priority: 'high' },
          // Critical fonts
          { 
            href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap', 
            as: 'style', 
            priority: 'high',
            crossOrigin: 'anonymous'
          },
          // Hero images
          { href: '/lovable-uploads/2f6c15f0-0e51-4e82-83ca-b7895b7e3f3e.png', as: 'image', priority: 'high' }
        ];

        criticalResources.forEach((resource, index) => {
          setTimeout(() => {
            try {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.href = resource.href;
              link.as = resource.as;
              
              if (resource.crossOrigin) {
                link.crossOrigin = resource.crossOrigin;
              }
              
              if (resource.priority === 'high') {
                link.setAttribute('importance', 'high');
              }
              
              document.head.appendChild(link);
            } catch (error) {
              logger.warn('Failed to preload critical resource', { 
                component: 'ProductionOptimizer',
                action: 'preloadCriticalResources' 
              }, { resource: resource.href, error });
            }
          }, index * 100);
        });
      };

      // Enhanced canonical URL handling
      const addCanonicalUrl = () => {
        const existingCanonical = document.querySelector('link[rel="canonical"]');
        if (!existingCanonical) {
          const canonical = document.createElement('link');
          canonical.rel = 'canonical';
          canonical.href = window.location.href.split('?')[0]; // Remove query params
          document.head.appendChild(canonical);
        }
      };

      // Enhanced SEO meta tags
      const enhanceSEOTags = () => {
        // Add robots meta if missing
        if (!document.querySelector('meta[name="robots"]')) {
          const robots = document.createElement('meta');
          robots.name = 'robots';
          robots.content = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
          document.head.appendChild(robots);
        }

        // Add theme color
        if (!document.querySelector('meta[name="theme-color"]')) {
          const themeColor = document.createElement('meta');
          themeColor.name = 'theme-color';
          themeColor.content = '#8B5CF6'; // Primary brand color
          document.head.appendChild(themeColor);
        }

        // Add apple touch icon
        if (!document.querySelector('link[rel="apple-touch-icon"]')) {
          const appleTouchIcon = document.createElement('link');
          appleTouchIcon.rel = 'apple-touch-icon';
          appleTouchIcon.href = '/icon-192.png';
          document.head.appendChild(appleTouchIcon);
        }
      };

      // Enhanced performance monitoring
      const monitorPerformance = () => {
        if ('performance' in window) {
          // Core Web Vitals monitoring
          const observeWebVitals = () => {
            try {
              // Largest Contentful Paint
              const lcpObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];
                if (lastEntry && window.gtag) {
                  window.gtag('event', 'web_vitals', {
                    event_category: 'Web Vitals',
                    event_label: 'LCP',
                    value: Math.round(lastEntry.startTime)
                  });
                }
              });
              lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

              // First Input Delay
              const fidObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach(entry => {
                  const fidEntry = entry as any; // Cast to any for FID properties
                  if (window.gtag) {
                    window.gtag('event', 'web_vitals', {
                      event_category: 'Web Vitals',
                      event_label: 'FID',
                      value: Math.round(fidEntry.processingStart - fidEntry.startTime)
                    });
                  }
                });
              });
              fidObserver.observe({ entryTypes: ['first-input'] });

              // Cumulative Layout Shift
              const clsObserver = new PerformanceObserver((list) => {
                let clsValue = 0;
                const entries = list.getEntries();
                entries.forEach(entry => {
                  const clsEntry = entry as any; // Cast to any for CLS properties
                  if (!clsEntry.hadRecentInput) {
                    clsValue += clsEntry.value;
                  }
                });
                if (clsValue > 0 && window.gtag) {
                  window.gtag('event', 'web_vitals', {
                    event_category: 'Web Vitals',
                    event_label: 'CLS',
                    value: Math.round(clsValue * 1000)
                  });
                }
              });
              clsObserver.observe({ entryTypes: ['layout-shift'] });
            } catch (error) {
              logger.warn('Web Vitals monitoring setup failed', { 
                component: 'ProductionOptimizer',
                action: 'observeWebVitals' 
              }, error);
            }
          };

          // Page load monitoring
          window.addEventListener('load', () => {
            const loadTime = performance.now();
            logger.info('Page load completed', { 
              component: 'ProductionOptimizer',
              action: 'pageLoad' 
            }, { loadTime: Math.round(loadTime) });
            
            if (window.gtag) {
              window.gtag('event', 'page_load_time', {
                event_category: 'Performance',
                value: Math.round(loadTime)
              });
            }

            // Start Web Vitals monitoring after load
            setTimeout(observeWebVitals, 100);
          });

          // Navigation monitoring
          const originalPushState = history.pushState;
          const originalReplaceState = history.replaceState;
          
          history.pushState = function(...args) {
            measurePerformance('Route Change', () => {
              return originalPushState.apply(this, args);
            });
          };
          
          history.replaceState = function(...args) {
            return originalReplaceState.apply(this, args);
          };
        }
      };

      // Enhanced Service Worker with update detection
      const registerServiceWorker = () => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/sw.js')
            .then(registration => {
              logger.info('Service Worker registered successfully', { 
                component: 'ProductionOptimizer',
                action: 'registerServiceWorker' 
              });
              
              // Listen for updates
              registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (newWorker) {
                  newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                      logger.info('New Service Worker content available', { 
                        component: 'ProductionOptimizer',
                        action: 'serviceWorkerUpdate' 
                      });
                    }
                  });
                }
              });
            })
            .catch(error => {
              logger.warn('Service Worker registration failed', { 
                component: 'ProductionOptimizer',
                action: 'registerServiceWorker' 
              }, error);
            });
        }
      };

      // Prefetch likely pages
      const prefetchLikelyPages = () => {
        const likelyPages = ['/feed', '/disney-world-tips', '/disney-photography', '/disney-trip-planning'];
        setTimeout(() => {
          likelyPages.forEach((page, index) => {
            setTimeout(() => {
              const link = document.createElement('link');
              link.rel = 'prefetch';
              link.href = page;
              document.head.appendChild(link);
            }, index * 500);
          });
        }, 3000);
      };

      // Execute all optimizations
      preloadCriticalResources();
      addCanonicalUrl();
      enhanceSEOTags();
      addResourceHints();
      monitorPerformance();
      registerServiceWorker();
      prefetchLikelyPages();

      // Enhanced logging
      logger.info('Production optimizations initialized successfully', { 
        component: 'ProductionOptimizer',
        action: 'initialization' 
      }, {
        features: [
          'SEO optimizations enhanced',
          'Performance monitoring with Web Vitals',
          'Resource preloading optimized',
          'Service worker with update detection',
          'Prefetching likely pages',
          'Bundle optimization tracking'
        ]
      });
      
      // Initialize bundle optimizations
      if (typeof window !== 'undefined' && (window as any).initBundleOptimizations) {
        (window as any).initBundleOptimizations();
      }
    }
  }, []);

  return null;
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}